import { useEffect, useRef } from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import { insights, investments, realEstate, residentialListings } from "@/data";

const BASE = "https://www.richcube.in";
const BRAND = "Richcube Wealth Management";

const PAGES = {
  "/": {
    title: "Real Estate & Investments Indore, Goa | Richcube",
    description: "Richcube Wealth Management — 20+ years of trusted real estate and investment expertise in Indore and Goa. Property, mutual funds, insurance and more.",
  },
  "/about": {
    title: "About Us — 20+ Years of Trust | Richcube",
    description: "The story of Richcube Wealth Management — 20+ years of earned trust across real estate and investments, with offices in Indore and Goa.",
  },
  "/team": {
    title: "Our Advisors & Experts | Richcube",
    description: "Meet the advisors behind Richcube Wealth Management — 57+ combined years across property strategy, wealth advisory and commercial leasing.",
  },
  "/why-richcube": {
    title: "Why Choose Richcube Wealth Management",
    description: "20+ years of market experience, real estate and investments under one roof, and local expertise in Indore and Goa. Discover the Richcube difference.",
  },
  "/blog": {
    title: "Market Insights & Investment Tips | Richcube",
    description: "Market context, local observations and practical thinking from Richcube Wealth Management — real estate and investment insights for the long view.",
  },
  "/contact": {
    title: "Contact Us — Indore & Goa Offices | Richcube",
    description: "Talk to Richcube Wealth Management — offices in Indore and Goa. Call +91 93006 16777 or book a consultation for property and investment advice.",
  },
  "/real-estate/residential-airbnb": {
    title: "Residential & Airbnb Properties Goa | Richcube",
    description: "Handpicked residences and Airbnb-ready apartments in Goa and Indore — luxury studio at Porvorim, North Goa from ₹1.03 Cr, near Mandovi River.",
  },
};

function resolveMeta(pathname) {
  if (PAGES[pathname]) return PAGES[pathname];
  const article = insights.find((item) => pathname === `/blog/${item.slug}`);
  if (article) return { title: `${article.title} | Richcube`, description: article.excerpt };
  const property = realEstate.find((item) => pathname === `/real-estate/${item.slug}`);
  if (property) return { title: `${property.title} Indore & Goa | Richcube`, description: property.description };
  const investment = investments.find((item) => pathname === `/investments/${item.slug}`);
  if (investment) return { title: `${investment.title} Indore & Goa | Richcube`, description: investment.description };
  const residence = residentialListings.find((item) => pathname === `/real-estate/residential-airbnb/${item.slug}`);
  if (residence) return { title: `${residence.title}, ${residence.location} | Richcube`, description: `${residence.title} — ${residence.type} in ${residence.location} by ${residence.developer}. ${residence.price}. Enquire with Richcube Wealth Management.` };
  return PAGES["/"];
}

function ensureAnalytics() {
  const id = process.env.REACT_APP_GA_MEASUREMENT_ID;
  if (!id || window.gtag) return;
  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${id}`;
  document.head.appendChild(script);
  window.dataLayer = window.dataLayer || [];
  window.gtag = function () { window.dataLayer.push(arguments); };
  window.gtag("js", new Date());
  window.gtag("config", id, { send_page_view: false });
}

export default function RouteMeta() {
  const location = useLocation();
  const meta = resolveMeta(location.pathname);
  const canonical = `${BASE}${location.pathname}`;
  const verification = process.env.REACT_APP_GOOGLE_SITE_VERIFICATION;

  const firstRender = useRef(true);

  useEffect(() => {
    ensureAnalytics();
    if (firstRender.current) { firstRender.current = false; return; }
    if (window.gtag) {
      window.gtag("event", "page_view", {
        page_path: location.pathname + location.search,
        page_title: meta.title,
        page_location: `${window.location.origin}${location.pathname}`,
      });
    }
  }, [location.pathname, location.search, meta.title]);

  return (
    <Helmet>
      <title>{meta.title}</title>
      <meta name="description" content={meta.description} />
      <link rel="canonical" href={canonical} />
      <meta property="og:title" content={meta.title} />
      <meta property="og:description" content={meta.description} />
      <meta property="og:url" content={canonical} />
      <meta name="twitter:title" content={meta.title} />
      <meta name="twitter:description" content={meta.description} />
      {verification ? <meta name="google-site-verification" content={verification} /> : null}
    </Helmet>
  );
}
